import React, { createContext, useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

type Language = "ko" | "en";

interface LanguageContextType {
  language: Language;
  changeLanguage: (lng: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState<Language>(() => {
    // localStorage에서 언어 가져오기, 없으면 'ko'
    return (localStorage.getItem("i18nextLng") as Language) || "ko";
  });

  // 컴포넌트 마운트 시 i18n 언어 설정
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const changeLanguage = async (lng: Language) => {
    try {
      await i18n.changeLanguage(lng);
      setLanguage(lng);
      localStorage.setItem("i18nextLng", lng);
    } catch (error) {
      console.error("Language change failed:", error);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
